import React, { createContext, useContext, useState, ReactNode } from "react";
import { FirstStepFormData } from "../schemas/validationSchemas";
import { PumpRecommendations as PumpRecommendationsType } from "../hooks/useFormPoolCalculations";


interface PumpRecommendationContextData {
  formData: FirstStepFormData | null;
  recommendations: PumpRecommendationsType | null;
  isCalculating: boolean;
  error: string | null;
  reportId: string | null;
  setFormData: (data: FirstStepFormData) => void;
  setRecommendations: (recommendations: PumpRecommendationsType) => void;
  setIsCalculating: (isCalculating: boolean) => void;
  setError: (error: string | null) => void;
  setReportId: (reportId: string | null) => void;
  clearResults: () => void;
}


const PumpRecommendationContext = createContext<
  PumpRecommendationContextData | undefined
>(undefined);


interface PumpRecommendationProviderProps {
  children: ReactNode;
}

export const PumpRecommendationProvider: React.FC<
  PumpRecommendationProviderProps
> = ({ children }) => {
  const [formData, setFormData] = useState<FirstStepFormData | null>(null);
  const [recommendations, setRecommendations] =
    useState<PumpRecommendationsType | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<string | null>(null);

  const clearResults = () => {
    setFormData(null);
    setRecommendations(null);
    setError(null);
    setIsCalculating(false);
    setReportId(null);
  };

  const value: PumpRecommendationContextData = {
    formData,
    recommendations,
    isCalculating,
    error,
    reportId,
    setFormData,
    setRecommendations,
    setIsCalculating,
    setError,
    setReportId,
    clearResults,
  };

  return (
    <PumpRecommendationContext.Provider value={value}>
      {children}
    </PumpRecommendationContext.Provider>
  );
};


export const usePumpRecommendationContext =
  (): PumpRecommendationContextData => {
    const context = useContext(PumpRecommendationContext);
    if (!context) {
      throw new Error(
        "usePumpRecommendationContext must be used within a PumpRecommendationProvider"
      );
    }
    return context;
  };
